import { useEffect, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import FetalStrip from './FetalStrip';
import {
  FHR_MIN, FHR_MAX, TOCO_MIN, TOCO_MAX, WINDOW_SEC, FETAL_PATTERNS,
} from '../../engine/fetalEngine';
import styles from './FetalMonitor.module.css';

const FHR_COLOR = '#FF4D8D';   // magenta — fetal, distinct from maternal green ECG
const TOCO_COLOR = '#5CD6FF';  // cyan — uterine activity

const VARIABILITY_LABEL = {
  absent: 'Absent (<2)', minimal: 'Minimal (<5)', moderate: 'Moderate (6–25)', marked: 'Marked (>25)',
};
const DECEL_LABEL = {
  none: 'None', early: 'Early', late: 'Late', variable: 'Variable', prolonged: 'Prolonged',
};

export default function FetalMonitor({ layout = 'full' }) {
  const fetal = useSimStore((s) => s.fetal);
  const patientName = useSimStore((s) => s.patientName);

  // Live clock for the header.
  const [clock, setClock] = useState('');
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString([], { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const preset = FETAL_PATTERNS[fetal.pattern];
  const category = preset?.category ? `Cat ${preset.category}` : '';
  const windowSec = fetal.windowSec ?? WINDOW_SEC;
  const windowMin = windowSec / 60;
  const windowLabel = Number.isInteger(windowMin) ? `${windowMin}` : windowMin.toFixed(1);

  return (
    <div className={`${styles.screen} ${layout === 'strip' ? styles.strip : ''}`}>
      <div className={styles.header}>
        <span className={styles.title}>CTG · INTRAPARTUM FETAL MONITORING</span>
        <span className={styles.headMeta}>{patientName}</span>
        <span className={styles.headClock}>{clock}</span>
      </div>

      {/* ── FHR channel ─────────────────────────────────────────────────── */}
      <div className={styles.channelRow}>
        <div className={styles.stripWrap}>
          <span className={styles.channelTag} style={{ color: FHR_COLOR }}>FHR bpm</span>
          <FetalStrip
            channel="fhr"
            fetal={fetal}
            min={FHR_MIN} max={FHR_MAX} gridStep={30}
            windowSec={windowSec} color={FHR_COLOR} normalBand={[110, 160]}
          />
        </div>
        <div className={styles.numericCol}>
          <div className={styles.bigNumericLabel}>FHR</div>
          <div className={styles.bigNumeric} style={{ color: FHR_COLOR }}>{Math.round(fetal.fhrBaseline)}</div>
          <div className={styles.bigNumericUnit}>bpm baseline</div>
          <div className={styles.subReadout}>
            <span className={styles.subLabel}>Variability</span>
            <span className={styles.subValue}>{VARIABILITY_LABEL[fetal.variability] ?? fetal.variability}</span>
          </div>
          <div className={styles.subReadout}>
            <span className={styles.subLabel}>Accels</span>
            <span className={styles.subValue}>{fetal.accels ? 'Present' : 'Absent'}</span>
          </div>
          <div className={styles.subReadout}>
            <span className={styles.subLabel}>Decels</span>
            <span className={styles.subValue}>{fetal.sinusoidal ? 'Sinusoidal' : (DECEL_LABEL[fetal.decel] ?? fetal.decel)}</span>
          </div>
          {category && <div className={styles.catBadge}>{category}</div>}
        </div>
      </div>

      {/* ── Tocodynamometer channel ─────────────────────────────────────── */}
      <div className={`${styles.channelRow} ${styles.tocoRow}`}>
        <div className={styles.stripWrap}>
          <span className={styles.channelTag} style={{ color: TOCO_COLOR }}>TOCO</span>
          <FetalStrip
            channel="toco"
            fetal={fetal}
            min={TOCO_MIN} max={TOCO_MAX} gridStep={25}
            windowSec={windowSec} color={TOCO_COLOR} normalBand={null}
          />
        </div>
        <div className={styles.numericCol}>
          <div className={styles.bigNumericLabel}>UA</div>
          <div className={styles.bigNumeric} style={{ color: TOCO_COLOR }}>{fetal.contractionFreq}</div>
          <div className={styles.bigNumericUnit}>ctx / 10 min</div>
          <div className={styles.subReadout}>
            <span className={styles.subLabel}>Strength</span>
            <span className={styles.subValue}>{fetal.contractionFreq > 0 ? `${fetal.contractionStrength}` : '—'}</span>
          </div>
          <div className={styles.subReadout}>
            <span className={styles.subLabel}>Resting tone</span>
            <span className={styles.subValue}>{fetal.uterineTone}</span>
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <span>{windowLabel} min window{windowSec > WINDOW_SEC ? ' · zoomed for trends' : ''}</span>
        {preset && <span className={styles.footerPattern}>{preset.name}</span>}
      </div>
    </div>
  );
}
